import TagManager from 'react-gtm-module';
import { IntercomAPI } from 'react-intercom';
import mixpanel from 'mixpanel-browser';
import MD5 from 'crypto-js/md5';

import { AnalyticsServices } from '../enums/analytics-types';

let USER_INFO = {};
window.USER_INFO = {};

export const Analytics = {
  init: () => {
    if (!window._env_.ANALYTICS_ACTIVE) {
      return;
    }

    TagManager.initialize({ gtmId: window._env_.GOOGLE_TAG_MANAGER_APP_ID });
    mixpanel.init(window._env_.MIXPANEL_APP_ID, {
      debug: window._env_.MIXPANEL_DEBUG,
      persistence: 'localStorage',
    });
  },

  track: (
    eventCategory,
    eventAction,
    options = {
      services: [AnalyticsServices.all],
      extraParams: {},
      joinCategory: true,
      disableIntercomCategory: false,
    },
  ) => {
    if (!window._env_.ANALYTICS_ACTIVE) {
      console.log(
        `%c Analytics event ${eventCategory}.${eventAction} triggered`,
        'color: cyan;font-size:1rem;',
        { event: `${eventCategory}.${eventAction}`, data: options },
      );
      return;
    }

    const userParams = {};
    const joinCategory = typeof options.joinCategory === 'undefined' ? true : options.joinCategory;
    const params = options.extraParams ? options.extraParams : {};
    const servicesToUse = options.services ? options.services : [AnalyticsServices.all];
    const eventName =
      joinCategory && eventCategory ? `${eventCategory}.${eventAction}` : eventAction;
    userParams.isMasquerade = USER_INFO.masquerade;
    params.isMasquerade = USER_INFO.masquerade;
    const deviceInfo = {
      screen: `${window?.innerWidth}x${window?.innerHeight}`,
      device: window?.navigator?.userAgent,
    };

    if (USER_INFO.distinctId) {
      userParams.userId = USER_INFO.distinctId;
    }

    servicesToUse.forEach((service) => {
      switch (service) {
        case AnalyticsServices.googleAnalytics:
          TagManager.dataLayer({
            dataLayer: {
              event: 'gaEvent',
              gaEventCategory: eventCategory,
              gaEventAction: eventAction,
              ...deviceInfo,
              ...userParams,
            },
          });
          break;
        case AnalyticsServices.intercom:
          IntercomAPI('trackEvent', options.disableIntercomCategory ? eventAction : eventName);
          break;
        case AnalyticsServices.mixpanel:
          mixpanel.track(eventName, { ...deviceInfo, params });
          break;
        default:
          TagManager.dataLayer({
            dataLayer: {
              event: 'gaEvent',
              gaEventCategory: eventCategory,
              gaEventAction: eventAction,
              ...userParams,
            },
          });
          IntercomAPI('trackEvent', eventName);
          mixpanel.track(eventName, params);
          break;
      }
    });
  },

  setUserInfo: (user) => {
    if (!window._env_.ANALYTICS_ACTIVE) {
      return;
    }

    const userInfo = {
      distinctId: MD5(user.email).toString(),
      name: user.name,
      email: user.email,
      created_at: user.created,
      userType: user.userType,
      company: user.company.name,
      introducerType: user.introducerType,
      consultant: user.consultant,
      masquerade: user.masquerade,
      membership: user?.membership?.level?.name,
      companyType: user?.company?.type,
      entityStatus: user?.company?.entityStatus,
    };

    if (USER_INFO.distinctId && USER_INFO.distinctId === userInfo.distinctId) {
      return;
    }

    USER_INFO = userInfo;
    // TODO - Temporary solution to support Analytics on remotes and container
    window.USER_INFO = USER_INFO;

    mixpanel.identify(USER_INFO.distinctId);
    mixpanel.people.set_once(userInfo);
  },

  setUserExtraInfo: (extraProps) => {
    if (!USER_INFO.distinctId) {
      return;
    }
    mixpanel.people.set_once(extraProps);
  },

  setCoursesInfo: (courses) => {
    if (!window._env_.INTERCOM_ACTIVE) {
      return;
    }

    const coursesInfo = {};

    if (courses.size) {
      coursesInfo.lessonsLaunched = courses.reduce(
        (acc, course) => +acc + +(course.isCore() ? course.getTotalLessonsStarted() : 0),
        0,
      );
      coursesInfo.lessonsCompleted = courses.reduce(
        (acc, course) => +acc + +(course.isCore() ? course.lessonsCompleted : 0),
        0,
      );
      coursesInfo.coreCourseCompleted = courses.find((course) => course.isCore())?.isCompleted();
      coursesInfo.coreLastLessonCompletedDate = courses
        .find((course) => course.isCore())
        ?.getLastLessonCompletedDate();

      coursesInfo.lessonsAdviserLaunched = courses.reduce(
        (acc, course) => +acc + +(course.isAdviser() ? course.getTotalLessonsStarted() : 0),
        0,
      );
      coursesInfo.lessonsAdviserCompleted = courses.reduce(
        (acc, course) => +acc + +(course.isAdviser() ? course.lessonsCompleted : 0),
        0,
      );
      coursesInfo.adviserCourseCompleted = courses
        .find((course) => course.isAdviser())
        ?.isCompleted();
      coursesInfo.adviserLastLessonCompletedDate = courses
        .find((course) => course.isAdviser())
        ?.getLastLessonCompletedDate();

      coursesInfo.lessonsMasteryLaunched = courses.reduce(
        (acc, course) => +acc + +(course.isMastery() ? course.getTotalLessonsStarted() : 0),
        0,
      );
      coursesInfo.lessonsMasteryCompleted = courses.reduce(
        (acc, course) => +acc + +(course.isMastery() ? course.lessonsCompleted : 0),
        0,
      );
      coursesInfo.masteryCourseCompleted = courses
        .find((course) => course.isMastery())
        ?.isCompleted();
      coursesInfo.masteryLastLessonCompletedDate = courses
        .find((course) => course.isMastery())
        ?.getLastLessonCompletedDate();
    }

    IntercomAPI('update', coursesInfo);
  },

  appendUserInfo: ({ membership }) => {
    if (!USER_INFO.distinctId) {
      return;
    }
    mixpanel.people.set({ membership });
  },
};
